import React, { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Col,
  Flex,
  Progress,
  Row,
  Spin,
  Statistic,
  Switch,
  Table,
  Typography,
} from 'antd'
import type { TableProps } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import {
  getStats,
  getHealth,
  getFunnel,
  getTransfers,
  toggleTransfers,
} from '@/api/ai-chat/overview'
import type {
  DashboardStats,
  DashboardHealth,
  DashboardFunnel,
  ErrorLogEntry,
} from '@/api/ai-chat/types'
import {
  OUTCOME_COLORS,
  STAGE_NAMES,
  SYS_LABELS,
  SYS_STATUS_MAP,
  fmtModel,
  fmtSysValue,
  fmtTime,
} from './utils'

const { Title, Text } = Typography

const MODEL_COLORS: Record<string, string> = {
  haiku: '#2d9e5f',
  sonnet: '#f57c00',
  openai: '#e53935',
}

function modelColor(model: string | null | undefined): string {
  if (!model) return ''
  if (model.includes('haiku')) return MODEL_COLORS.haiku
  if (model.includes('sonnet')) return MODEL_COLORS.sonnet
  if (model.includes('gpt') || model.includes('openai'))
    return MODEL_COLORS.openai
  return ''
}

const errorLogColumns: TableProps<ErrorLogEntry>['columns'] = [
  {
    title: 'Time',
    dataIndex: 'checked_at',
    render: (v: string) => (
      <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
        {fmtTime(v)}
      </Text>
    ),
  },
  {
    title: 'Model',
    dataIndex: 'model',
    render: (v: string) => fmtModel(v),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (v: string) => (
      <Text type={v === 'down' ? 'danger' : 'warning'} strong>
        {v}
      </Text>
    ),
  },
  {
    title: 'Response',
    dataIndex: 'response_ms',
    render: (v: number | null) => (v != null ? v + 'ms' : '—'),
  },
  {
    title: 'Active Model',
    dataIndex: 'active_model',
    render: (v: string) => fmtModel(v),
  },
  {
    title: 'Action',
    dataIndex: 'action',
    render: (v: string | null) =>
      v ? (
        <Text style={{ color: '#2d5be3', fontWeight: 600, fontSize: 11 }}>
          {v}
        </Text>
      ) : (
        '—'
      ),
  },
]

export const OverviewPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [health, setHealth] = useState<DashboardHealth | null>(null)
  const [funnel, setFunnel] = useState<DashboardFunnel | null>(null)
  const [transfersEnabled, setTransfersEnabled] = useState<boolean>(false)
  const [transfersLoading, setTransfersLoading] = useState(false)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [statsData, healthData, funnelData, transfersData] =
        await Promise.all([
          getStats(),
          getHealth(),
          getFunnel(),
          getTransfers(),
        ])
      setStats(statsData)
      setHealth(healthData)
      setFunnel(funnelData)
      setTransfersEnabled(transfersData.enabled)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleToggleTransfers = async () => {
    setTransfersLoading(true)
    try {
      const res = await toggleTransfers()
      setTransfersEnabled(res.enabled)
    } finally {
      setTransfersLoading(false)
    }
  }

  const maxStageCount = Math.max(
    ...(funnel?.stages?.map((s) => s.count) ?? [1]),
    1,
  )
  const totalOutcomes =
    funnel?.outcomes?.reduce((s, o) => s + Number(o.count), 0) || 1

  return (
    <Spin spinning={loading}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Overview
        </Title>
        <Button icon={<ReloadOutlined />} onClick={load}>
          Refresh
        </Button>
      </Flex>

      {/* Stat cards */}
      {stats && health && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={8} lg={4}>
            <Card>
              <Statistic
                title="Current"
                value={stats.current}
                valueStyle={
                  stats.current > 0 ? { color: '#2d9e5f' } : undefined
                }
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                active now
              </Text>
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card>
              <Statistic title="Today" value={stats.today} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                conversations
              </Text>
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card>
              <Statistic
                title="This Week"
                value={stats.week_appointments}
                valueStyle={{ color: '#2d9e5f' }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                appointments of {stats.week_total}
              </Text>
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card>
              <Statistic
                title="Conversion"
                value={stats.conversion_rate}
                suffix="%"
                valueStyle={{ color: '#2d5be3' }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                all time
              </Text>
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card>
              <Statistic title="Avg Stage" value={stats.avg_stage ?? '—'} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                reached
              </Text>
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card>
              <Statistic
                title="Active Model"
                value={fmtModel(health.current_model)}
                valueStyle={{
                  fontSize: 18,
                  color: modelColor(health.current_model),
                }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {health.last_switch_at
                  ? 'since ' + fmtTime(health.last_switch_at)
                  : 'no switches'}
              </Text>
            </Card>
          </Col>
          {stats.pending_bookings > 0 && (
            <Col xs={12} sm={8} lg={4}>
              <Card>
                <Statistic
                  title="Pending"
                  value={stats.pending_bookings}
                  valueStyle={{ color: '#f57c00' }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  booking retries
                </Text>
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* System health */}
      {health && (
        <Card
          title="System Health"
          extra={
            <Flex align="center" gap={8}>
              <Text
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: transfersEnabled ? '#16a34a' : '#dc2626',
                }}
              >
                Transfers {transfersEnabled ? 'On' : 'Off'}
              </Text>
              <Switch
                checked={transfersEnabled}
                loading={transfersLoading}
                onChange={handleToggleTransfers}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
            </Flex>
          }
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[10, 10]}>
            {(health.system ?? []).map((s) => (
              <Col key={s.service} xs={12} sm={8} md={6} lg={4}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Badge
                    status={SYS_STATUS_MAP[s.status] ?? 'default'}
                    text={
                      <Text
                        strong
                        style={{
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px',
                        }}
                      >
                        {SYS_LABELS[s.service] ?? s.service}
                      </Text>
                    }
                  />
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {fmtSysValue(s)}
                    </Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Funnel + Outcomes */}
      {funnel && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <Card title="Stage Funnel">
              {(funnel.stages ?? []).map((s) => (
                <Flex
                  key={s.stage}
                  align="center"
                  gap={8}
                  style={{ marginBottom: 10 }}
                >
                  <Text
                    type="secondary"
                    style={{ width: 80, fontSize: 12, flexShrink: 0 }}
                  >
                    {STAGE_NAMES[s.stage] ?? 'Stage ' + s.stage}
                  </Text>
                  <div style={{ flex: 1 }}>
                    <Progress
                      percent={Math.round((s.count / maxStageCount) * 100)}
                      showInfo={false}
                      strokeColor="#2d9e5f"
                      size="small"
                    />
                  </div>
                  <Text
                    strong
                    style={{ width: 40, textAlign: 'right', fontSize: 12 }}
                  >
                    {s.count}
                  </Text>
                </Flex>
              ))}
              {(funnel.stages?.length ?? 0) === 0 && (
                <Text type="secondary">No data yet</Text>
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Outcomes">
              <Flex vertical gap={8}>
                {(funnel.outcomes ?? []).map((o) => (
                  <Flex key={o.outcome} align="center" gap={10}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: OUTCOME_COLORS[o.outcome] ?? '#ccc',
                        flexShrink: 0,
                      }}
                    />
                    <Text type="secondary" style={{ flex: 1, fontSize: 12 }}>
                      {o.outcome}
                    </Text>
                    <Text strong style={{ fontSize: 12 }}>
                      {o.count}
                    </Text>
                    <Text
                      type="secondary"
                      style={{ fontSize: 11, width: 36, textAlign: 'right' }}
                    >
                      {Math.round((Number(o.count) / totalOutcomes) * 100)}%
                    </Text>
                  </Flex>
                ))}
                {(funnel.outcomes?.length ?? 0) === 0 && (
                  <Text type="secondary">No data yet</Text>
                )}
              </Flex>
            </Card>
          </Col>
        </Row>
      )}

      {/* Error log */}
      {health && (
        <Card
          title="Model Error Log"
          extra={
            <Text type="secondary" style={{ fontSize: 11 }}>
              downs, slowness &amp; switches — 14 day window
            </Text>
          }
        >
          {(health.error_log?.length ?? 0) === 0 ? (
            <Text type="secondary">No errors in the last 14 days</Text>
          ) : (
            <Table<ErrorLogEntry>
              dataSource={health.error_log ?? []}
              columns={errorLogColumns}
              rowKey={(r) => r.checked_at + r.model}
              pagination={false}
              size="small"
            />
          )}
        </Card>
      )}
    </Spin>
  )
}
